import type { UIMessage, UITools } from 'ai';
import type { ObjectId } from 'mongodb';
import { z } from 'zod';

export enum ChatRole {
  user = 'user',
  assistant = 'assistant',
  system = 'system',
}

export enum Rating {
  bad = 'bad',
  good = 'good',
}

// AI SDK UIMessage parts. `looseObject` preserves SDK-specific fields (providerMetadata, tool input/output, etc.)
// without having to mirror the SDK schema. We only validate the shapes we actively use
export const textPartSchema = z.object({ text: z.string(), type: z.literal('text') });
const reasoningPartSchema = z.looseObject({ text: z.string(), type: z.literal('reasoning') });
const stepStartPartSchema = z.object({ type: z.literal('step-start') });
const unknownPartSchema = z.looseObject({ type: z.string().min(1).max(50) });

export const messagePartSchema = z.union([textPartSchema, reasoningPartSchema, stepStartPartSchema, unknownPartSchema]);

export const messageSchema = z.object({
  id: z.string().min(1),
  parts: z.array(messagePartSchema).min(1),
  rating: z.enum(Rating).optional(),
  role: z.enum(ChatRole),
  sentAt: z.date(),
  text: z.string(),
});
export type Message = z.infer<typeof messageSchema>;

export const feedbackInputSchema = z.object({
  id: z.string().min(1),
  rating: z.enum(Rating),
});

export type Conversation = {
  createdAt: Date;
  messages: Message[];
  sessionId: string;
  updatedAt: Date;
  vid: ObjectId;
};

type UIMetadata = { rating?: Rating };

// AI SDK's UIMessage shape with our metadata attached. Distinct from `Message` used for persistence.
export type UiMsg<Tools extends UITools = UITools> = UIMessage<UIMetadata, never, Tools>;
